import React, { useState } from "react";
import { motion } from "framer-motion";
import OtpInput from "../../design-system/OTPInput";
import { Text } from "../../design-system/Text";
import { Img } from "../../design-system/Img";
import { transferFunds } from "../../../../network/Apiservices";
import { initToast } from "../../../../network/serverErrorHandler";
import { transferFormType } from "../../../home/types";
import InstructionsViewer from "./InstructionsViewer";
import StSecureCommSession from "../../../SecureCommunication";
import { StCryptoSymmetricOperator } from "../../../SecureCommunication/StCryptoSymmetricOperator";
import { useLocationContext } from "../../../location/context/locationContext";
import { getCurrentCurrency } from "../../../../utils/currencyFormatter";

interface PropType {
  toggleTotp: () => void;
  transferData: any;
  navigateToResultModal: (
    status: transferFormType["approvalStatus"],
    errCode?: string
  ) => void;
  from?: string;
  portfolioSubmitHandler?: (otp?: string, manual?: boolean) => void;
  otpError?: string;
  isSecureChannelEnabled?: boolean;
  breakPoints: any;
}

const ApproveTotpTransfer: React.FC<PropType> = ({
  toggleTotp,
  transferData,
  navigateToResultModal,
  portfolioSubmitHandler,
  otpError: propOtpError,
  isSecureChannelEnabled,
  breakPoints,
}) => {
  const [state, setState] = useState({
    otp: "",
    otpError: "",
    numberOfAttempts: 0,
    currentImageIndex: 0,
  });

  const { locationData } = useLocationContext();

  const submitHandler = async (otp: string) => {
    if (portfolioSubmitHandler) {
      portfolioSubmitHandler(otp, true);
      return;
    }

    const { transactionType, selectedAccount, toAccount, payee } = transferData;

    try {
      const payload = {
        transferType: transactionType,
        fromAccount: selectedAccount?.value.id,
        toAccount:
          transactionType === "OWN_ACCOUNT_TRANSFER"
            ? toAccount?.value.id
            : payee?.value.id,
        amount: `${transferData.amount}`,
        fromCurrency: getCurrentCurrency().code,
        toCurrency: getCurrentCurrency().code,
        narration: transferData.narration,
        beneficiaryId: transferData.beneficiary?.value.id,
        payeeName:
          transferData.toAccount?.value.name ||
          transferData.beneficiary?.value.name,
        toCountry:
          transferData.transactionType === "INTERNATIONAL_TRANSFER"
            ? transferData.toCountry ?? "AE"
            : undefined,
        swiftCode:
          transferData.transactionType === "INTERNATIONAL_TRANSFER"
            ? transferData.toCountry?.swiftCodePrefix ?? "AE"
            : undefined,
        sourceOfFunds:
          transferData.transactionType === "INTERNATIONAL_TRANSFER"
            ? transferData.narration
            : undefined,
        beneficiaryName:
          transferData.toAccount?.value.name ??
          transferData.beneficiary?.value.name,
      };

      if (transferData.mfaPayload) {
        let computedWeight = transferData.mfaPayload.weight.includes("STOTP")
          ? transferData.mfaPayload.weight.replace("STOTP", "TOTP")
          : transferData.mfaPayload.weight;

        let challengePayload = {
          transactionId: transferData.mfaPayload.transactionId,
          weight: computedWeight,
          totp: otp,
        };

        if (
          transferData.mfaPayload.weight !== "SMS" &&
          isSecureChannelEnabled
        ) {
          try {
            let symmetricKey =
              StSecureCommSession.getInstance().getSymmetricKey();
            let encryptedOtp =
              await StCryptoSymmetricOperator.getInstance().encrypt(
                otp,
                symmetricKey!
              );
            console.log("encryptedOtp :", encryptedOtp);
            console.log(
              "urlEncodedEncryptedOtp :",
              encodeURIComponent(encryptedOtp)
            );

            challengePayload.totp = encryptedOtp;
          } catch (error) {
            console.log("error failed to encrypt otp", error);
          }
        }

        let res = await transferFunds(
          transferData.transactionType,
          payload,
          challengePayload,
          locationData
        );

        if (res) {
          if (res.headers["X-EK-MFA-CHALLENGE-PAYLOAD"]) {
            const mfaPayload = JSON.parse(res.headers["X-EK-MFA-CHALLENGE-PAYLOAD"]);
            console.log("res.headers", res.headers);
            console.log("mfaPayload", mfaPayload);

            setState((ps) => ({
              ...ps,
              otpError: "Invalid PIN. Try using the correct PIN",
              numberOfAttempts: mfaPayload.numberOfAttempts,
            }));
            return;
          } else {
            if (
              res.data.status === "COMPLETED" ||
              res.data.status === "PENDING"
            ) {
              navigateToResultModal(res.data.status);
            }
          }
        }
      }
    } catch (error: any) {
      if (error.response) {
        console.log("error.response__:", error.response);

        if (error.response.data.errorCode === "ERR_902") {
          navigateToResultModal("DECLINED", error.response.data.errorCode);
          return;
        }
        if (error.response.data.errorCode === "ERR_901") {
          navigateToResultModal("TIMEDOUT", error.response.data.errorCode);
          return;
        } else {
          initToast(error.message);
        }
      } else {
        initToast(error.message);
      }
    }
  };

  const otpComplete = (otp: string) => {
    if (otp.length < 6) {
      setState((prev) => ({ ...prev, otpError: "Please enter a valid OTP" }));
      return;
    }
    submitHandler(otp);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="flex flex-1 flex-col gap-10 items-center justify-around p-5"
    >
      <div className="flex flex-col gap-5 w-full justify-between items-center h-[101.09px]">
        <div className="flex flex-row w-full px-5">
          {transferData.mfaPayload.weight !== "SMS" && (
            <div
              className="lg:flex-[0.04] xs:flex-[0.1]"
              role="button"
              tabIndex={0}
              onKeyDown={toggleTotp}
              onClick={toggleTotp}
            >
              <Img src={"arrow-narrow-left.svg"} />
            </div>
          )}
          <div className="flex-1 animate-slideDown-d-1_5 initial-hidden">
            <Text className="text-black-800 text-lg font-bold align-middle self-center">
              Enter OTP here to approve
            </Text>
          </div>
        </div>
        <div className="animate-slideDown-d-1_5 initial-hidden">
          <OtpInput
            length={6}
            isError={Boolean(state.otpError)}
            onComplete={otpComplete}
          />
        </div>
        {(propOtpError || state.otpError) && (
          <Text className="text-red-500 text-sm">
            {propOtpError ?? state.otpError}
            {" - "}
            {`Number of Attempts: ${
              transferData.mfaPayload.numberOfAttempts
                ? transferData.mfaPayload.numberOfAttempts
                : state.numberOfAttempts
            }`}
          </Text>
        )}
      </div>
      <div className="flex sm:w-[300px] lg:w-[600px] xl:w-[803px] border border-b-1 border-t-0 border-dashed border-blue_gray-300 animate-slideDown-d-2 initial-hidden" />

      {transferData?.mfaPayload && (
        <InstructionsViewer challengeType={transferData?.mfaPayload.weight} />
      )}

      {breakPoints?.lg &&
        (transferData?.mfaPayload.weight === "SMS" ? (
          <div className="flex absolute flex-row w-[803px] bottom-[10%] z-0 animate-slideUp-d-1 initial-hidden">
            <div className="relative flex flex-row left-[364px] items-center">
              <div className="w-[18px] h-[18px] gap-0 bg-gray-50_02 rounded-lg" />
              <div className="border-gray-50_02 border-dashed border-b-2 h-0 w-[35px] mx-[0.5px]" />
              <div className="w-[18px] h-[18px] gap-0 bg-gray-50_02 rounded-lg" />
            </div>
          </div>
        ) : (
          <div className="flex absolute flex-row w-[803px] bottom-[10%] z-0 animate-slideUp-d-1 initial-hidden">
            <div className="relative flex flex-row left-[225px] items-center">
              <div className="w-[18px] h-[18px] gap-0 bg-gray-50_02 rounded-lg" />
              <div className="border-gray-50_02 border-dashed border-b-2 h-0 w-[35px] mx-[0.5px]" />
              <div className="w-[18px] h-[18px] gap-0 bg-gray-50_02 rounded-lg" />
            </div>
            <div className="relative flex flex-row left-[434px] items-center">
              <div className="w-[18px] h-[18px] gap-0 bg-gray-50_02 rounded-lg" />
              <div className="border-gray-50_02 border-dashed border-b-2 h-0 w-[34px] mx-[0.5px]" />
              <div className="w-[18px] h-[18px] gap-0 bg-gray-50_02 rounded-lg" />
            </div>
          </div>
        ))}
    </motion.div>
  );
};

export default ApproveTotpTransfer;
